import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { db } from '../../firebase/firebase-config';
import { getLadaPorZona } from '../../helpers/util';
import { types } from '../../types/types';
import Swal from 'sweetalert2';

const ZONAS_POR_PAIS = {
    'MX': 'America/Mexico_City', 'CO': 'America/Bogota', 'PE': 'America/Lima',
    'CL': 'America/Santiago', 'AR': 'America/Argentina/Buenos_Aires', 'BR': 'America/Sao_Paulo',
    'VE': 'America/Caracas', 'EC': 'America/Guayaquil', 'BO': 'America/La_Paz',
    'PY': 'America/Asuncion', 'UY': 'America/Montevideo', 'PA': 'America/Panama',
    'CR': 'America/Costa_Rica', 'GT': 'America/Guatemala', 'HN': 'America/Tegucigalpa',
    'NI': 'America/Managua', 'SV': 'America/El_Salvador', 'DO': 'America/Santo_Domingo',
    'CU': 'America/Havana', 'PR': 'America/Puerto_Rico',
};

export const MiNegocioScreen = ({ onBack }) => {
    const dispatch = useDispatch();
    const { hashtag, zonaHoraria, ladaPais } = useSelector(state => state.auth);
    const lada = ladaPais || '521';
    const bandera = lada === '521' ? '🇲🇽' : lada === '57' ? '🇨🇴' : lada === '51' ? '🇵🇪' :
        lada === '56' ? '🇨🇱' : lada === '54' ? '🇦🇷' : lada === '55' ? '🇧🇷' : '🌎';

    const [nombre, setNombre] = useState('');
    const [direccion, setDireccion] = useState('');
    const [mapsLink, setMapsLink] = useState('');
    const [telefonos, setTelefonos] = useState(['']);
    const [tolerancia, setTolerancia] = useState(false);
    const [minutos, setMinutos] = useState('10');
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const inputRef = useRef(null);
    const autocompleteRef = useRef(null);

    // Refs para acceder a valores actuales desde el listener sin reinicializar
    const ladaRef = useRef(lada);
    const direccionRef = useRef(direccion);
    const zonaHorariaRef = useRef(zonaHoraria);
    const hashtagRef = useRef(hashtag);

    useEffect(() => { ladaRef.current = lada; }, [lada]);
    useEffect(() => { direccionRef.current = direccion; }, [direccion]);
    useEffect(() => { zonaHorariaRef.current = zonaHoraria; }, [zonaHoraria]);
    useEffect(() => { hashtagRef.current = hashtag; }, [hashtag]);

    useEffect(() => {
        const cargar = async () => {
            try {
                const snap = await db.doc(`general/${hashtag}`).get();
                const data = snap.data() || {};
                setNombre(data.nombre || '');
                setDireccion(data.direccion || '');
                setMapsLink(data.maps || '');
                if (data.telefonos_contacto?.length > 0) {
                    setTelefonos(data.telefonos_contacto.map(t => t.slice(-10)));
                }
                if (data.tolerancia && data.tolerancia !== '-1') {
                    setTolerancia(true);
                    setMinutos(data.tolerancia);
                }
            } catch (e) {
                console.log('Error:', e);
            } finally {
                setCargando(false);
            }
        };
        cargar();
    }, [hashtag]);

    // Inicializar autocomplete después de que cargue el componente
    useEffect(() => {
        if (cargando) return; // esperar a que carguen los datos
        if (!window.google || !inputRef.current) return;
        if (autocompleteRef.current) return; // ya inicializado
        try {
            autocompleteRef.current = new window.google.maps.places.Autocomplete(
                inputRef.current, { types: ['establishment', 'geocode'] }
            );
            autocompleteRef.current.addListener('place_changed', () => {
                const place = autocompleteRef.current.getPlace();
                if (!place || (!place.formatted_address && !place.name)) return;

                // Validar mismo país usando ref
                if (place.address_components) {
                    const paisComp = place.address_components.find(c => c.types.includes('country'));
                    if (paisComp) {
                        const zonaDetectada = ZONAS_POR_PAIS[paisComp.short_name];
                        const ladaDetectada = zonaDetectada ? getLadaPorZona(zonaDetectada) : null;
                        if (ladaDetectada && ladaDetectada !== ladaRef.current) {
                            Swal.fire({
                                icon: 'warning',
                                title: 'País diferente',
                                text: 'No es posible cambiar la dirección a otro país. Si necesitas hacer este cambio, contacta a soporte.',
                                confirmButtonColor: '#D4537E',
                            });
                            if (inputRef.current) inputRef.current.value = direccionRef.current;
                            return;
                        }
                    }
                }

                const addr = place.formatted_address || place.name;
                setDireccion(addr);

                // Actualizar link de Maps
                if (place.url) {
                    setMapsLink(place.url);
                } else if (place.geometry) {
                    setMapsLink(`https://www.google.com/maps?q=${place.geometry.location.lat()},${place.geometry.location.lng()}`);
                }

                // Actualizar zona horaria con Timezone API
                if (place.geometry) {
                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();
                    const timestamp = Math.floor(Date.now() / 1000);
                    fetch(`https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`)
                        .then(r => r.json())
                        .then(async tz => {
                            if (tz.timeZoneId && tz.timeZoneId !== zonaHorariaRef.current) {
                                const nuevaZona = tz.timeZoneId;
                                const nuevaLada = getLadaPorZona(nuevaZona);
                                await db.doc(`general/${hashtagRef.current}`).update({ zonaHoraria: nuevaZona, ladaPais: nuevaLada });
                                dispatch({ type: types.authUpdateEmpresa, payload: { zonaHoraria: nuevaZona, ladaPais: nuevaLada } });
                                const colSnap = await db.collection(`general/${hashtagRef.current}/consultores`).get();
                                if (!colSnap.empty) {
                                    Swal.fire({
                                        icon: 'info',
                                        title: 'Zona horaria actualizada',
                                        html: `Tu dirección está en una zona horaria diferente (<strong>${nuevaZona}</strong>).<br><br>⚠️ Deberás actualizar manualmente la zona horaria en los calendarios de Google de tus colaboradoras para que las citas se muestren correctamente.`,
                                        confirmButtonColor: '#D4537E',
                                    });
                                }
                            }
                        })
                        .catch(() => {
                            // Fallback por estado para México
                            if (place.address_components) {
                                const paisComp = place.address_components.find(c => c.types.includes('country'));
                                const estadoComp = place.address_components.find(c => c.types.includes('administrative_area_level_1'));
                                let zona = paisComp ? ZONAS_POR_PAIS[paisComp.short_name] : null;
                                if (paisComp?.short_name === 'MX' && estadoComp) {
                                    const estado = estadoComp.long_name;
                                    if (/quintana roo/i.test(estado)) zona = 'America/Cancun';
                                    else if (/sonora/i.test(estado)) zona = 'America/Hermosillo';
                                    else if (/chihuahua|sinaloa|nayarit|durango/i.test(estado)) zona = 'America/Chihuahua';
                                    else if (/baja california$/i.test(estado)) zona = 'America/Tijuana';
                                    else if (/baja california sur/i.test(estado)) zona = 'America/Mazatlan';
                                    else zona = 'America/Mexico_City';
                                }
                                if (zona && zona !== zonaHorariaRef.current) {
                                    dispatch({ type: types.authUpdateEmpresa, payload: { zonaHoraria: zona, ladaPais: getLadaPorZona(zona) } });
                                    db.doc(`general/${hashtagRef.current}`).update({ zonaHoraria: zona, ladaPais: getLadaPorZona(zona) });
                                }
                            }
                        });
                }
            });
        } catch (e) {
            console.log('Autocomplete error:', e);
        }
    }, [cargando]); // Se ejecuta cuando termina de cargar

    const handleTelefono = (idx, valor) => {
        const soloNumeros = valor.replace(/\D/g, '').slice(0, 10);
        const nuevos = [...telefonos];
        nuevos[idx] = soloNumeros;
        setTelefonos(nuevos);
    };

    const handleGuardar = async () => {
        if (!nombre.trim()) { Swal.fire('Falta el nombre', 'El nombre del negocio es obligatorio.', 'warning'); return; }
        if (!direccion.trim()) { Swal.fire('Falta la dirección', 'La dirección es obligatoria.', 'warning'); return; }
        const telesValidos = telefonos.filter(t => t.length === 10);
        if (telesValidos.length === 0) { Swal.fire('Falta el teléfono', 'Agrega al menos un teléfono de 10 dígitos.', 'warning'); return; }

        setGuardando(true);
        try {
            const tolVal = tolerancia ? String(parseInt(minutos) || 10) : '-1';
            await db.doc(`general/${hashtag}`).update({
                nombre: nombre.trim(),
                direccion: direccion.trim(),
                maps: mapsLink || `https://www.google.com/maps/search/${encodeURIComponent(direccion)}`,
                telefonos_contacto: telesValidos.map(t => lada + t),
                tolerancia: tolVal,
            });
            dispatch({ type: types.authUpdateEmpresa, payload: { nombre: nombre.trim() } });
            Swal.fire({ icon: 'success', title: '¡Cambios guardados!', timer: 1500, showConfirmButton: false })
                .then(() => onBack());
        } catch (e) {
            Swal.fire('Error', 'No se pudo guardar. Intenta de nuevo.', 'error');
        } finally {
            setGuardando(false);
        }
    };

    if (cargando) return (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 32 }}>📍</div>
            <p style={{ color: 'var(--pink-800)' }}>Cargando...</p>
        </div>
    );

    return (
        <div className="app-container">
            <div className="app-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--pink-600)', fontSize: 20, cursor: 'pointer', padding: '0 4px' }}>←</button>
                <div>
                    <h1>Mi negocio</h1>
                    <p>{hashtag}</p>
                </div>
            </div>

            <div className="screen-body">
                <div className="info-box" style={{ marginTop: 0 }}>
                    <div className="info-box-body">
                        El identificador de tu negocio (<strong>{hashtag}</strong>) no puede cambiarse ya que está ligado a tu configuración en WhatsApp.
                    </div>
                </div>

                <div className="field-label" style={{ marginTop: 16 }}>Nombre del negocio</div>
                <input className="field-input" placeholder="Nombre de tu negocio"
                    value={nombre} onChange={e => setNombre(e.target.value)} />
                <p className="field-hint">Solo el nombre visible, el identificador no cambia</p>

                <div className="field-label" style={{ marginTop: 16 }}>Dirección</div>
                <input ref={inputRef} className="field-input"
                    placeholder="Busca tu negocio o escribe la dirección..."
                    value={direccion} onChange={e => setDireccion(e.target.value)} />
                <p className="field-hint">📍 Solo puedes cambiar la dirección dentro del mismo país</p>

                <div className="field-label" style={{ marginTop: 16 }}>Teléfonos de contacto</div>
                <p className="field-hint" style={{ marginBottom: 8 }}>Tus clientas podrán llamarte o escribirte aquí</p>
                {telefonos.map((tel, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                        <div style={{ padding: '12px', border: '0.5px solid var(--border)', borderRadius: 10, fontSize: 14, color: 'var(--pink-900)', whiteSpace: 'nowrap', background: 'var(--pink-50)' }}>
                            {bandera} +{lada}
                        </div>
                        <input className="field-input" style={{ flex: 1 }} placeholder="10 dígitos"
                            type="tel" inputMode="numeric" value={tel}
                            onChange={e => handleTelefono(idx, e.target.value)} maxLength={10} />
                        {idx > 0 && (
                            <button onClick={() => setTelefonos(telefonos.filter((_, i) => i !== idx))}
                                style={{ color: '#c62828', background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
                        )}
                    </div>
                ))}
                {telefonos.length < 3 && (
                    <button className="add-btn" onClick={() => setTelefonos([...telefonos, ''])}>
                        + Agregar otro teléfono
                    </button>
                )}

                <div className="field-label" style={{ marginTop: 20 }}>¿Das tolerancia de llegada?</div>
                <div className="toggle-row">
                    <div>
                        <div className="toggle-label">Sí, doy tolerancia</div>
                        <div className="toggle-sub">Le avisamos al confirmar la cita</div>
                    </div>
                    <div className={`toggle ${tolerancia ? 'on' : ''}`} onClick={() => setTolerancia(!tolerancia)}>
                        <div className="toggle-thumb" />
                    </div>
                </div>
                {tolerancia && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                        <input className="field-input" type="number" value={minutos}
                            onChange={e => setMinutos(e.target.value.replace(/\D/g, ''))}
                            style={{ width: 80 }} min={1} max={60} />
                        <span style={{ fontSize: 14, color: 'var(--pink-800)' }}>minutos de tolerancia</span>
                    </div>
                )}
            </div>

            <div className="screen-footer">
                <button className="btn-primary" onClick={handleGuardar} disabled={guardando}>
                    {guardando ? 'Guardando...' : 'Guardar cambios'}
                </button>
                <button className="btn-secondary" onClick={onBack}>Cancelar</button>
            </div>
        </div>
    );
};
